import React, { useState, useEffect } from 'react';
import { BomberosHeader } from '../../components/BomberosHeader';
import { useNavigate, useParams } from 'react-router-dom';
import { bomberosPersonalAPI, bomberosCompaniaAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Skeleton } from '../../components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import { ArrowLeft, Save, User, Loader2 } from 'lucide-react';

export default function BomberoFormPage() {
    const navigate = useNavigate();
    const { id } = useParams();
    const isEdit = Boolean(id);

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [companias, setCompanias] = useState([]);
    const [formData, setFormData] = useState({
        rut: '',
        nombres: '',
        apellido_paterno: '',
        apellido_materno: '',
        fecha_nacimiento: '',
        fecha_ingreso: new Date().toISOString().split('T')[0],
        grado: 'voluntario',
        cargo: '',
        compania_id: '',
        email: '',
        telefono: '',
        direccion: '',
        grupo_sanguineo: '',
        contacto_emergencia: '',
        telefono_emergencia: '',
        activo: true,
    });

    useEffect(() => {
        loadCompanias();
        if (isEdit) {
            loadBombero();
        }
    }, [id]);

    const loadCompanias = async () => {
        try {
            const res = await bomberosCompaniaAPI.getAll();
            setCompanias(res.data);
            if (!isEdit && res.data.length > 0 && !formData.compania_id) {
                setFormData(prev => ({ ...prev, compania_id: res.data[0].id }));
            }
        } catch (err) {
            toast.error('Error al cargar compañías');
        }
    };

    const loadBombero = async () => {
        setLoading(true);
        try {
            const res = await bomberosPersonalAPI.getById(id);
            setFormData({
                rut: res.data.rut || '',
                nombres: res.data.nombres || '',
                apellido_paterno: res.data.apellido_paterno || '',
                apellido_materno: res.data.apellido_materno || '',
                fecha_nacimiento: res.data.fecha_nacimiento || '',
                fecha_ingreso: res.data.fecha_ingreso || '',
                grado: res.data.grado || 'voluntario',
                cargo: res.data.cargo || '',
                compania_id: res.data.compania_id || '',
                email: res.data.email || '',
                telefono: res.data.telefono || '',
                direccion: res.data.direccion || '',
                grupo_sanguineo: res.data.grupo_sanguineo || '',
                contacto_emergencia: res.data.contacto_emergencia || '',
                telefono_emergencia: res.data.telefono_emergencia || '',
                activo: res.data.activo !== false,
            });
        } catch (err) {
            toast.error('Error al cargar bombero');
            navigate('/bomberos/personal');
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const formatRut = (value) => {
        // Remove non-alphanumeric characters
        let rut = value.replace(/[^0-9kK]/g, '');
        if (rut.length > 1) {
            // Add dots and dash
            const dv = rut.slice(-1);
            let num = rut.slice(0, -1);
            num = num.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
            rut = `${num}-${dv}`;
        }
        return rut.toUpperCase();
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.rut || !formData.nombres || !formData.apellido_paterno || !formData.compania_id) {
            toast.error('Complete los campos obligatorios');
            return;
        }

        setSaving(true);
        try {
            if (isEdit) {
                await bomberosPersonalAPI.update(id, formData);
                toast.success('Bombero actualizado correctamente');
            } else {
                await bomberosPersonalAPI.create({
                    ...formData,
                    empresa_id: localStorage.getItem('empresa_id') || ''
                });
                toast.success('Bombero creado correctamente');
            }
            navigate('/bomberos/personal');
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Error al guardar');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="space-y-6 p-6">
                <Skeleton className="h-10 w-64" />
                <Skeleton className="h-96 w-full" />
            </div>
        );
    }

    return (
        <>
            <BomberosHeader />
            <div className="space-y-6 p-6 pt-20 max-w-4xl mx-auto">
            {/* Header */}
            <div className="flex items-center gap-4">
                <Button variant="ghost" size="icon" onClick={() => navigate('/bomberos/personal')} data-testid="back-btn">
                    <ArrowLeft className="h-5 w-5" />
                </Button>
                <div>
                    <h1 className="text-2xl font-bold flex items-center gap-2">
                        <User className="h-6 w-6" />
                        {isEdit ? 'Editar Bombero' : 'Nuevo Bombero'}
                    </h1>
                    <p className="text-muted-foreground">
                        {isEdit ? 'Modifique los datos del bombero' : 'Complete los datos para registrar un nuevo bombero'}
                    </p>
                </div>
            </div>

            <form onSubmit={handleSubmit}>
                {/* Datos Personales */}
                <Card className="border-2 mb-6">
                    <CardHeader>
                        <CardTitle className="text-lg">Datos Personales</CardTitle>
                        <CardDescription>Información básica del bombero</CardDescription>
                    </CardHeader>
                    <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label htmlFor="rut">RUT *</Label>
                            <Input
                                id="rut"
                                placeholder="12.345.678-9"
                                value={formData.rut}
                                onChange={(e) => handleChange('rut', formatRut(e.target.value))}
                                maxLength={12}
                                data-testid="input-rut"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="nombres">Nombres *</Label>
                            <Input
                                id="nombres"
                                placeholder="Juan Carlos"
                                value={formData.nombres}
                                onChange={(e) => handleChange('nombres', e.target.value)}
                                data-testid="input-nombres"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="apellido_paterno">Apellido Paterno *</Label>
                            <Input
                                id="apellido_paterno"
                                placeholder="Pérez"
                                value={formData.apellido_paterno}
                                onChange={(e) => handleChange('apellido_paterno', e.target.value)}
                                data-testid="input-apellido-paterno"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="apellido_materno">Apellido Materno</Label>
                            <Input
                                id="apellido_materno"
                                placeholder="González"
                                value={formData.apellido_materno}
                                onChange={(e) => handleChange('apellido_materno', e.target.value)}
                                data-testid="input-apellido-materno"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="fecha_nacimiento">Fecha de Nacimiento</Label>
                            <Input
                                id="fecha_nacimiento"
                                type="date"
                                value={formData.fecha_nacimiento}
                                onChange={(e) => handleChange('fecha_nacimiento', e.target.value)}
                                data-testid="input-fecha-nacimiento"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="grupo_sanguineo">Grupo Sanguíneo</Label>
                            <Select value={formData.grupo_sanguineo} onValueChange={(v) => handleChange('grupo_sanguineo', v)}>
                                <SelectTrigger data-testid="select-grupo-sanguineo">
                                    <SelectValue placeholder="Seleccione grupo" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="A+">A+</SelectItem>
                                    <SelectItem value="A-">A-</SelectItem>
                                    <SelectItem value="B+">B+</SelectItem>
                                    <SelectItem value="B-">B-</SelectItem>
                                    <SelectItem value="AB+">AB+</SelectItem>
                                    <SelectItem value="AB-">AB-</SelectItem>
                                    <SelectItem value="O+">O+</SelectItem>
                                    <SelectItem value="O-">O-</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </CardContent>
                </Card>

                {/* Datos Institucionales */}
                <Card className="border-2 mb-6">
                    <CardHeader>
                        <CardTitle className="text-lg">Datos Institucionales</CardTitle>
                        <CardDescription>Información de la institución</CardDescription>
                    </CardHeader>
                    <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label htmlFor="compania_id">Compañía *</Label>
                            <Select value={formData.compania_id} onValueChange={(v) => handleChange('compania_id', v)}>
                                <SelectTrigger data-testid="select-compania">
                                    <SelectValue placeholder="Seleccione compañía" />
                                </SelectTrigger>
                                <SelectContent>
                                    {companias.map(c => (
                                        <SelectItem key={c.id} value={c.id}>{c.nombre}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="grado">Grado *</Label>
                            <Select value={formData.grado} onValueChange={(v) => handleChange('grado', v)}>
                                <SelectTrigger data-testid="select-grado">
                                    <SelectValue placeholder="Seleccione grado" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="voluntario">Voluntario</SelectItem>
                                    <SelectItem value="bombero">Bombero</SelectItem>
                                    <SelectItem value="teniente">Teniente</SelectItem>
                                    <SelectItem value="capitan">Capitán</SelectItem>
                                    <SelectItem value="comandante">Comandante</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="cargo">Cargo</Label>
                            <Input
                                id="cargo"
                                placeholder="Director, Secretario, etc."
                                value={formData.cargo}
                                onChange={(e) => handleChange('cargo', e.target.value)}
                                data-testid="input-cargo"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="fecha_ingreso">Fecha de Ingreso *</Label>
                            <Input
                                id="fecha_ingreso"
                                type="date"
                                value={formData.fecha_ingreso}
                                onChange={(e) => handleChange('fecha_ingreso', e.target.value)}
                                data-testid="input-fecha-ingreso"
                            />
                        </div>
                        <div className="space-y-2 md:col-span-2">
                            <Label htmlFor="activo">Estado</Label>
                            <Select value={formData.activo ? 'true' : 'false'} onValueChange={(v) => handleChange('activo', v === 'true')}>
                                <SelectTrigger data-testid="select-activo">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="true">Activo</SelectItem>
                                    <SelectItem value="false">Inactivo (Baja)</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </CardContent>
                </Card>

                {/* Contacto */}
                <Card className="border-2 mb-6">
                    <CardHeader>
                        <CardTitle className="text-lg">Información de Contacto</CardTitle>
                    </CardHeader>
                    <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label htmlFor="email">Correo Electrónico</Label>
                            <Input
                                id="email"
                                type="email"
                                placeholder="correo@ejemplo.com"
                                value={formData.email}
                                onChange={(e) => handleChange('email', e.target.value)}
                                data-testid="input-email"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="telefono">Teléfono</Label>
                            <Input
                                id="telefono"
                                placeholder="+56 9 1234 5678"
                                value={formData.telefono}
                                onChange={(e) => handleChange('telefono', e.target.value)}
                                data-testid="input-telefono"
                            />
                        </div>
                        <div className="space-y-2 md:col-span-2">
                            <Label htmlFor="direccion">Dirección</Label>
                            <Textarea
                                id="direccion"
                                placeholder="Calle, número, comuna, ciudad"
                                value={formData.direccion}
                                onChange={(e) => handleChange('direccion', e.target.value)}
                                rows={2}
                                data-testid="input-direccion"
                            />
                        </div>
                    </CardContent>
                </Card>

                {/* Contacto de Emergencia */}
                <Card className="border-2 mb-6">
                    <CardHeader>
                        <CardTitle className="text-lg">Contacto de Emergencia</CardTitle>
                    </CardHeader>
                    <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label htmlFor="contacto_emergencia">Nombre Contacto</Label>
                            <Input
                                id="contacto_emergencia"
                                placeholder="Nombre del contacto"
                                value={formData.contacto_emergencia}
                                onChange={(e) => handleChange('contacto_emergencia', e.target.value)}
                                data-testid="input-contacto-emergencia"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="telefono_emergencia">Teléfono Emergencia</Label>
                            <Input
                                id="telefono_emergencia"
                                placeholder="+56 9 1234 5678"
                                value={formData.telefono_emergencia}
                                onChange={(e) => handleChange('telefono_emergencia', e.target.value)}
                                data-testid="input-telefono-emergencia"
                            />
                        </div>
                    </CardContent>
                </Card>

                {/* Actions */}
                <div className="flex justify-end gap-4">
                    <Button type="button" variant="outline" onClick={() => navigate('/bomberos/personal')}>
                        Cancelar
                    </Button>
                    <Button type="submit" disabled={saving} data-testid="submit-btn">
                        {saving ? (
                            <>
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                Guardando...
                            </>
                        ) : (
                            <>
                                <Save className="h-4 w-4 mr-2" />
                                {isEdit ? 'Actualizar' : 'Crear Bombero'}
                            </>
                        )}
                    </Button>
                </div>
            </form>
        </div>
        </>
    );
}
